import { groupBy } from "~/tools/group-by";
import { ImmutableMap } from "~/tools/immutable-map";
import { isDeepEqual } from "~/tools/is-deep-equal";
import { mapValues } from "~/tools/map-values";
import { max } from "~/tools/max";
import { times } from "~/tools/times";
import { toValues } from "~/tools/to-values";

import type { StreamConfigFromManager } from ".";

export interface StreamConfig<TType extends string = string> {
	streamType: TType;
}

interface StreamEntry<TStream> {
	listeners: number;
	stream: TStream;
}

/**
 * The StreamRegistry class is responsible for managing the streams and their listeners.
 * Every stream is configured via a StreamConfig object only once and can have as many listeners as needed.
 * As soon as the last listener is removed, the stream is removed from the registry.
 */
export class StreamRegistry<TManager> {
	/**
	 * Initializes the StreamRegistry with the StreamManager schema.
	 * It provides a type-safe way to manage streams based on the manager schema.
	 */
	public static init<TManager>(manager: TManager): StreamRegistry<TManager> {
		return new StreamRegistry(manager, ImmutableMap.empty({ isValueEqual: isDeepEqual }));
	}

	private constructor(
		/**
		 * The manager parameter is never used but it holds the StreamManager type info.
		 * So it makes sure that the registry is always created via the StreamManger#manage() method.
		 */
		protected readonly manager: TManager,
		private readonly streams: ImmutableMap<string, StreamEntry<StreamConfigFromManager<TManager>>>,
	) {}

	private update(
		streams: ImmutableMap<string, StreamEntry<StreamConfigFromManager<TManager>>>,
	): StreamRegistry<TManager> {
		if (streams === this.streams) {
			return this;
		}

		return new StreamRegistry(this.manager, streams);
	}

	/**
	 * Creates a new stream in the registry OR increases the number of listeners.
	 */
	public add<TStream extends StreamConfigFromManager<TManager>>(
		key: string,
		stream: TStream,
	): StreamRegistry<TManager> {
		return this.update(
			this.streams.update(key, (current) => {
				if (current == null) {
					return { stream, listeners: 1 };
				}

				return { ...current, listeners: current.listeners + 1 };
			}),
		);
	}

	/**
	 * Remove a stream from the registry by a key OR decrease the number of listeners.
	 */
	public remove(key: string): StreamRegistry<TManager> {
		return this.update(
			this.streams.update(key, (current) => {
				if (current == null || current.listeners <= 1) {
					return undefined;
				}

				return { ...current, listeners: current.listeners - 1 };
			}),
		);
	}

	/**
	 * Converts all registered streams to groups of streams.
	 * A stream type can exist only once in a group.
	 * This way it makes sure that every group can be handed by a single WebSocket connection.
	 */
	public toGroups(): Array<ImmutableMap<string, StreamConfigFromManager<TManager>>> {
		const groups = groupBy(
			this.streams.toArray().map(([, { stream }]) => stream),
			({ streamType }) => streamType,
		);

		// there should be at least 1 websocket open to avoid rapid reconnects
		const longestGroup = max(
			1,
			toValues(groups).map((streams) => streams.length),
		);

		return times(longestGroup, (index) => {
			const group = mapValues(groups, (streams) => streams.at(index));

			return ImmutableMap.fromRecord<string, StreamConfigFromManager<TManager>>(group);
		});
	}
}
